<script setup lang="ts">
/**
 * InviteModal — modal for inviting a new board member.
 * Emits 'close' when the modal should be dismissed.
 */
import { ref } from 'vue';
import BaseButton from '@/components/ui/BaseButton.vue';
import { useBoardMembersStore } from '@/stores/boardMembers';
import type { BoardRole } from '@/api/boardTypes';

const emit = defineEmits<{
  close: [];
}>();

const store = useBoardMembersStore();

const email = ref('');
const role = ref<BoardRole>('BoardMember');
const allowSms = ref(false);
const isSubmitting = ref(false);
const submitError = ref<string | null>(null);

const ROLES: { value: BoardRole; label: string }[] = [
  { value: 'BoardMember', label: 'Board Member' },
  { value: 'President', label: 'President' },
  { value: 'Treasurer', label: 'Treasurer' },
  { value: 'Secretary', label: 'Secretary' },
  { value: 'MaintenanceChair', label: 'Maintenance Chair' },
  { value: 'PropertyManager', label: 'Property Manager' },
];

async function submit() {
  if (!email.value.trim()) return;
  isSubmitting.value = true;
  submitError.value = null;
  try {
    await store.invite({
      email: email.value.trim(),
      role: role.value,
      allowSms: allowSms.value,
    });
    emit('close');
  } catch (e) {
    submitError.value = e instanceof Error ? e.message : 'Failed to send invitation.';
  } finally {
    isSubmitting.value = false;
  }
}
</script>

<template>
  <!-- Backdrop -->
  <div
    class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-fg/30"
    role="dialog"
    aria-modal="true"
    aria-label="Invite a board member"
    @click.self="emit('close')"
  >
    <div class="bg-surface border border-border rounded-xl shadow-lg w-full max-w-md">
      <!-- Header -->
      <div class="flex items-start gap-3 p-5 border-b border-border">
        <div
          class="w-9 h-9 rounded-md bg-navy-100 text-navy-700 grid place-items-center flex-shrink-0"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4">
            <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="8.5" cy="7" r="4" />
            <path d="M20 8v6M17 11h6" />
          </svg>
        </div>
        <div class="flex-1 min-w-0">
          <div class="text-card text-fg">Invite a board member</div>
          <div class="text-caption text-muted mt-0.5">
            They'll get an email with a link to join your Cypress Ridge HOA workspace.
          </div>
        </div>
        <button
          class="w-8 h-8 grid place-items-center text-muted hover:text-fg hover:bg-canvas-soft rounded-md transition-colors"
          aria-label="Close"
          @click="emit('close')"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Body -->
      <div class="p-5 flex flex-col gap-4">
        <!-- Email -->
        <label class="flex flex-col gap-1.5">
          <span class="text-caption font-semibold text-fg-soft uppercase tracking-wide">Email</span>
          <input
            v-model="email"
            type="email"
            placeholder="name@example.com"
            class="h-9 px-3 text-body border border-border rounded-md bg-surface focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-navy-500"
            data-testid="invite-email-input"
          >
        </label>

        <!-- Role -->
        <label class="flex flex-col gap-1.5">
          <span class="text-caption font-semibold text-fg-soft uppercase tracking-wide">Role</span>
          <select
            v-model="role"
            class="h-9 px-3 text-body border border-border rounded-md bg-surface focus:outline-none focus:ring-2 focus:ring-navy-500"
            data-testid="invite-role-select"
          >
            <option v-for="r in ROLES" :key="r.value" :value="r.value">{{ r.label }}</option>
          </select>
        </label>

        <!-- SMS opt-in -->
        <label class="flex items-center gap-2.5 cursor-pointer">
          <input
            v-model="allowSms"
            type="checkbox"
            class="w-4 h-4 accent-navy-700"
          >
          <span class="text-body text-fg-soft">
            Allow SMS notifications
            <span class="text-muted">(requires phone verification on first login)</span>
          </span>
        </label>

        <!-- Error -->
        <p v-if="submitError" class="text-caption text-red-700">{{ submitError }}</p>
      </div>

      <!-- Footer -->
      <div class="flex items-center justify-end gap-2 px-5 pb-5">
        <BaseButton variant="ghost" size="md" @click="emit('close')">Cancel</BaseButton>
        <BaseButton
          variant="primary"
          size="md"
          :loading="isSubmitting"
          data-testid="invite-submit-btn"
          @click="submit"
        >
          Send invitation
        </BaseButton>
      </div>
    </div>
  </div>
</template>
